/**
 * L5: Settings (01-references §6 S12). It copies 1Password iOS Settings > Security: Face ID,
 * Auto-lock, Lock Now, and the clipboard setting. The clipboard row is informational here,
 * because a web app cannot clear on a timer (01 §3). Vault additions: what this copy is (source,
 * age, wallets) and the build version.
 */
import type { AppActions, AppState } from "../contract.js";
import { backButton, cell, group, navBar, sheetFrame } from "../shell/chrome.js";
import { h, icon } from "../shell/dom.js";
import { AUTO_LOCK_OPTIONS, autoLockLabel } from "../lock/session.js";
import { replicaAge } from "../vm/staleness.js";

const UNLOCK_METHOD_LABEL: Record<AppState["unlockMethod"], string> = {
  placeholder: "Placeholder",
  "webauthn-prf": "Passkey (PRF)",
  none: "None",
};

export function renderSettings(state: AppState, actions: AppActions): HTMLElement {
  const snap = state.snapshot!;
  const age = replicaAge(snap.asOf, state.now);
  const faceId = h("input", { type: "checkbox", class: "switch", role: "switch", "aria-label": "Unlock with Face ID", checked: true, disabled: true });
  return h(
    "main",
    { class: "screen", "data-screen": "settings" },
    navBar({ left: backButton("Back", actions), largeTitle: "Settings" }),
    group(
      "Security",
      [
        cell({ label: "Unlock with Face ID", lead: icon("face"), accessory: faceId }),
        cell({ label: "Auto-lock", lead: icon("lock"), detail: autoLockLabel(state.autoLock), chevron: true, onClick: () => actions.openSheet({ kind: "autolock" }) }),
        cell({ label: "Lock Now", cls: "action", onClick: () => actions.lock() }),
      ],
      {
        small: true,
        foot:
          state.unlockMethod === "placeholder"
            ? "Face ID is a placeholder in this build: the button unlocks without checking. The real unlock uses a passkey and arrives with the live vault. There is no password to type, by design."
            : "Unlock uses a passkey protected by Face ID. There is no password to type, by design.",
      },
    ),
    group("Clipboard", [cell({ label: "Clear copied items", detail: "On your next tap" })], {
      small: true,
      foot: "Web apps on iPhone can't clear the clipboard on a timer, so Vowed clears it the next time you tap in the app. It can't tell whether another app copied something since.",
    }),
    group(
      "This Copy",
      [
        cell({ label: "Data source", detail: snap.fixture ? "Fixture data (fake)" : "Vault replica" }),
        cell({ label: "Updated", detail: age.label.replace(/^Updated /, "") }),
        cell({ label: "Wallets", detail: String(snap.wallets.length) }),
        cell({ label: "Access", detail: "Read-only" }),
      ],
      { small: true, foot: age.level === "stale" ? "This copy may be out of date. Open Vowed on your Mac to refresh it." : undefined },
    ),
    group("About", [cell({ label: "Version", detail: h("span", { class: "cell-detail" }, state.version) }), cell({ label: "Unlock method", detail: UNLOCK_METHOD_LABEL[state.unlockMethod] })], { small: true }),
  );
}

export function renderAutoLockSheet(state: AppState, actions: AppActions): HTMLElement {
  return sheetFrame(
    "Auto-lock",
    actions,
    h("p", { class: "sheet-note" }, "Lock Vowed after you leave the app."),
    group(
      null,
      AUTO_LOCK_OPTIONS.map((s) => cell({ label: autoLockLabel(s), check: state.autoLock === s, onClick: () => { actions.setAutoLock(s); actions.closeSheet(); } })),
    ),
  );
}
